import { useRef, useMemo } from 'react';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  HStack,
  Box,
  Text,
  Icon,
  Divider,
  Select,
  FormControl,
  Input,
} from '@chakra-ui/react';

import { ReactComponent as FiltersIcon } from '../assets/icons/filters.svg';
import { ReactComponent as SortsIcon } from '../assets/icons/sorts.svg';
import { DrawerFilter, DrawerFilterType, DrawerSort } from '../models';
import { useForm } from 'react-hook-form';
import { formatDate } from '../utils';
import { useSearchParams } from 'react-router-dom';

export const FilterDrawer = ({
  isOpen,
  onClose,
  filters,
  sorts,
}: {
  isOpen: boolean;
  onClose: (filterValues?: any) => void;
  filters: DrawerFilter[];
  sorts?: DrawerSort[];
}) => {
  const btnRef = useRef<HTMLElement>(null);
  const [_, setSearchParams] = useSearchParams();

  const defaultValues = useMemo(() => {
    const defaultFilterValues: { [k: string]: string | undefined } = {};
    filters.forEach(filter => {
      defaultFilterValues[filter.name] = filter.value;
    });

    return defaultFilterValues;
  }, [filters]);

  const { register, getValues } = useForm({
    defaultValues,
  });

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={btnRef}
    >
      <DrawerContent>
        <DrawerHeader>
          <HStack align="center">
            <HStack>
              <Box
                borderRadius="md"
                boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                border="1px"
                borderColor="custom_gray.4"
                px="2"
                py="1"
              >
                <Icon as={FiltersIcon} />
              </Box>
              <Text fontWeight="semibold" fontSize="md">
                Filters
              </Text>
            </HStack>
            <DrawerCloseButton />
          </HStack>
        </DrawerHeader>
        <Divider />

        <DrawerBody>
          {filters.map(({ label, type, list, name }) => (
            <Box key={name} mb="4">
              <Text fontWeight="medium" fontSize="sm">
                {label}
              </Text>

              <FormControl flexBasis="50%" mt="2">
                {type === DrawerFilterType.SELECT && (
                  <Select {...register(name)}>
                    <option />
                    {list?.map(({ text, value }) => (
                      <option value={value} key={value}>
                        {text}
                      </option>
                    ))}
                  </Select>
                )}

                {type === DrawerFilterType.DATE && (
                  <Input {...register(name)} type="date" />
                )}
              </FormControl>
            </Box>
          ))}

          {sorts && (
            <Box>
              <HStack mt="12" mb="1">
                <Box
                  borderRadius="md"
                  boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                  border="1px"
                  borderColor="custom_gray.4"
                  px="2"
                  py="1"
                >
                  <Icon as={SortsIcon} />
                </Box>
                <Text fontWeight="semibold" fontSize="md">
                  Sorting Options
                </Text>
              </HStack>
              <Divider />

              <Box mt="8" mb="4">
                <Text fontWeight="medium" fontSize="sm">
                  Sorts
                </Text>
                <FormControl mt="2">
                  <Select {...register('sortKey')}>
                    <option></option>
                    {sorts?.map(({ name, value }) => (
                      <option key={name} value={value}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box mb="4">
                <Text fontWeight="medium" fontSize="sm">
                  Sort Direction
                </Text>
                <FormControl mt="2">
                  <Select {...register('sortDirection')}>
                    <option></option>
                    <option value="ASC">Ascending</option>
                    <option value="DESC">Descending</option>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            onClick={() => {
              const filterValues = getValues();

              filters.forEach(filter => {
                if (
                  filter.type === DrawerFilterType.DATE &&
                  filterValues[filter.name] &&
                  typeof filterValues[filter.name] === 'string'
                ) {
                  filterValues[filter.name] = formatDate(
                    filterValues[filter.name]
                  );
                }
              });
              onClose(filterValues);
              setSearchParams(filterValues);
            }}
          >
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
